import React from "react";
import { Box, HStack, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { RATING_SCALE, RatingValue } from "./types";

/**
 * 5段階評価（◎/〇/△/▲/✖）のアイコン。
 * Unicode記号（"◎"等）をそのままテキスト描画すると、環境によってはOSの
 * 絵文字カラーフォントにフォールバックし、指定した color（currentColor）を
 * 無視した配色で表示されてしまうことがあるため、SVGで自前描画する。
 * stroke/fill に currentColor を使うことで、呼び出し側の color prop
 * （選択状態に応じた配色切り替えなど）にそのまま追従する。
 */
export const RatingIcon: React.FC<{ val: RatingValue; size?: number }> = ({ val, size = 18 }) => {
  switch (val) {
    case 5: // ◎ 非常に良い
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
          <circle cx="12" cy="12" r="3.2" fill="currentColor" />
        </svg>
      );
    case 4: // 〇 良い
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    case 3: // △ 普通
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 4.2 L20.8 19.4 L3.2 19.4 Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        </svg>
      );
    case 2: // ▲ やや不安
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 4.2 L20.8 19.4 L3.2 19.4 Z" fill="currentColor" stroke="none" strokeLinejoin="round" />
        </svg>
      );
    case 1: // ✖ 悪い
    default:
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
          <line x1="5" y1="5" x2="19" y2="19" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
          <line x1="19" y1="5" x2="5" y2="19" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
        </svg>
      );
  }
};

/**
 * design_handoff 準拠：カード内の丸番号バッジ＋見出し＋補足文言。
 * 例）① 検索条件を指定して検索を実行してください
 */
export const StepHeader: React.FC<{ step: number; title: React.ReactNode; note?: React.ReactNode }> = ({
  step,
  title,
  note,
}) => (
  <HStack spacing="8px" mb="14px" flexWrap="wrap">
    <Box
      as="span"
      display="inline-grid"
      placeItems="center"
      w="22px"
      h="22px"
      borderRadius="50%"
      bg="#16233f"
      color="#fff"
      fontSize="12px"
      fontWeight={700}
      flexShrink={0}
    >
      {step}
    </Box>
    <Text fontSize="14px" fontWeight={700} color="#16233f">
      {title}
    </Text>
    {note && (
      <Text fontSize="12px" color="#94a3b8">
        {note}
      </Text>
    )}
  </HStack>
);

/**
 * design_handoff 準拠：5段階評価（◎/〇/△/▲/✖）の凡例一覧。
 */
export const ScaleLegend: React.FC<{ namespace: string; mb?: string }> = ({ namespace, mb = "14px" }) => {
  const { t } = useTranslation();
  const tr = (key: string) => t(`features.InvestmentScreening.${namespace}.salesDataLinkage.${key}`);
  return (
    <Wrap spacing="18px" mb={mb} pt="14px" borderTop="1px solid #eef2f7">
      {RATING_SCALE.map((r) => (
        <WrapItem key={r.val}>
          <HStack spacing="6px" fontSize="12px" color="#64748b">
            <Box color={r.color} display="inline-flex">
              <RatingIcon val={r.val} size={16} />
            </Box>
            <Text>{tr(`ratingScale.${r.labelKey}`)}</Text>
          </HStack>
        </WrapItem>
      ))}
    </Wrap>
  );
};

/**
 * design_handoff 準拠：セクション見出し（色付きバー＋タイトル＋補足）。
 */
export const SectionTitle: React.FC<{ title: React.ReactNode; note?: React.ReactNode }> = ({ title, note }) => (
  <HStack spacing="10px" mb="16px" align="center" flexWrap="wrap">
    <Box w="6px" h="22px" borderRadius="3px" bg="#16233f" flexShrink={0} />
    <Text fontSize="19px" fontWeight={700} color="#16233f">
      {title}
    </Text>
    {note && (
      <Text fontSize="12px" color="#94a3b8">
        {note}
      </Text>
    )}
  </HStack>
);

/** design_handoff 準拠：虫眼鏡アイコン（検索系プレースホルダー・ボタン用） */
export const SearchIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

/** design_handoff 準拠：鍵アイコン（対象外プレースホルダー用） */
export const LockIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="10" width="16" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

/** design_handoff 準拠：AI（きらめき）アイコン */
export const SparkleIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z" />
    <path d="M19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9L19 15z" />
  </svg>
);

/**
 * design_handoff 準拠：検索前・対象外・未算出時などに表示するダッシュ枠プレースホルダー。
 */
export const PlaceholderNote: React.FC<{
  icon?: React.ReactNode;
  title: React.ReactNode;
  desc?: React.ReactNode;
  bg?: string;
  p?: string;
}> = ({ icon, title, desc, bg = "#fff", p = "64px 20px" }) => (
  <Box bg={bg} border="1px dashed #cbd5e1" borderRadius="10px" p={p} textAlign="center">
    {icon && (
      <Box
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        w="40px"
        h="40px"
        borderRadius="50%"
        bg="#eef2f7"
        color="#94a3b8"
        mb="10px"
      >
        {icon}
      </Box>
    )}
    <Text fontSize="14px" fontWeight={600} color="#64748b">
      {title}
    </Text>
    {desc && (
      <Text fontSize="12px" color="#94a3b8" mt="5px">
        {desc}
      </Text>
    )}
  </Box>
);
